import { useEffect, useRef, useState } from "react";

/**
 * Animated number — counts up to a value once it scrolls into view.
 * Supports inputs like: 300000, "300,000+", "−35%", "100K+", "6.2x", "₹186"
 * Non-numeric inputs (e.g. "0 → 100K+") fall back to rendering the raw value.
 */
export default function AnimatedNumber({ value, duration = 1600, className = "" }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [display, setDisplay] = useState(0);

  // Parse a single leading-or-embedded number out of the value
  const valueStr = typeof value === "number" ? String(value) : String(value ?? "");
  const match = valueStr.match(/-?\d[\d,]*(\.\d+)?/);
  const numericString = match ? match[0] : null;
  const numeric = numericString !== null ? parseFloat(numericString.replace(/,/g, "")) : null;
  const matchIndex = match ? match.index : -1;
  const prefix = match ? valueStr.slice(0, matchIndex) : "";
  const suffix = match ? valueStr.slice(matchIndex + numericString.length) : "";
  const hasDecimal = numericString ? numericString.includes(".") : false;
  const usesThousands = numericString ? numericString.includes(",") : false;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (numeric === null || isNaN(numeric)) { setStarted(true); return; }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting && !started) { setStarted(true); obs.unobserve(e.target); }
      }),
      { threshold: 0.35 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [numeric, started]);

  useEffect(() => {
    if (!started || numeric === null || isNaN(numeric)) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(numeric * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(numeric);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, numeric, duration]);

  if (numeric === null || isNaN(numeric)) {
    return <span ref={ref} className={className}>{valueStr}</span>;
  }

  let formatted;
  if (hasDecimal) {
    formatted = display.toFixed(1);
  } else if (usesThousands || Math.abs(numeric) >= 1000) {
    formatted = Math.round(display).toLocaleString();
  } else {
    formatted = String(Math.round(display));
  }

  return (
    <span ref={ref} className={className}>{prefix}{formatted}{suffix}</span>
  );
}
